import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Layout, Row, Col, Card, message, Alert, Drawer, Button, Popconfirm } from 'antd';
import posthog from 'posthog-js';
import { InvoiceFormBase, createBuyerInvoiceConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import InvoiceUploadandParse, {
	InvoiceLoadingSkeletonAnimated,
} from '../invoice_uploader/InvoiceUploadandParse';
import { adaptParsedInvoiceToFormData } from '../../utils/InvoiceDataAdapter';
import {
	createInvoiceForSupplierBasedOnWorkOrderForBuyer,
	createInvoiceForSupplierBasedOnWorkOrderForSupplier,
	createInvoiceByInternalTech,
	invoicesRestCrudThunksForBuyer,
	invoicesRestCrudThunksForSupplier,
	fetchTaxLineItemsForBuyer,
} from '../../thunks/invoices_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { compareParsedWithFinal, getFixedFloatStringOrZero } from '../../utils/InvoiceUtils';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { withUserType } from '../withUserType';

const queryString = require('qs');
const { Content } = Layout;

const NewBuyerInvoicePageV2: FC<any> = ({
	createInvoiceWithWorkOrderId,
	createInvoice,
	updateInvoice,
	fetchTaxLineItems,
	userType,
	currentUser,
	workOrder,
	creating,
	updating,
	createErrors,
	updateErrors,
}): React.ReactElement => {
	const [invoiceFormData, setInvoiceFormData] = useState<any>({});
	const [invoiceFormDataLoading, setInvoiceFormDataLoading] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [formKey, setFormKey] = useState(0);
	const [parsedDiscount, setParsedDiscount] = useState(null);
	const [parsedFormDataSnapshot, setParsedFormDataSnapshot] = useState<any>(null);
	const [parsingLoading, setParsingLoading] = useState(false);
	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();
	const [showDrawer, setShowDrawer] = useState(false);
	const [disableCosts, setDisableCosts] = useState(false);
	const workOrderId = useMemo(() => {
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}

		const queryParams = queryString.parse(searchString);
		return queryParams.workOrderId || match.params.id;
	}, [location.search, match.params.id]);

	useEffect(() => {
		if (workOrderId) {
			setInvoiceFormData((prevFormData) => {
				if (prevFormData.workOrderId !== workOrderId) {
					return {
						...prevFormData,
						workOrderId: workOrderId,

						externalWorkOrderId: prevFormData.externalWorkOrderId || String(workOrderId),
						supplierFacilityId:
							workOrder && workOrder.supplierFacilityId
								? workOrder.supplierFacilityId
								: prevFormData.supplierFacilityId,
					};
				}

				if (!prevFormData.externalWorkOrderId) {
					return {
						...prevFormData,
						externalWorkOrderId: String(workOrderId),
					};
				}
				return prevFormData;
			});
		}
	}, [workOrderId, workOrder]);

	const handleDeletePdf = () => {
		if (parsedFormDataSnapshot) {
			posthog.capture('invoice_pdf_deleted_after_parsing', {
				user_type: userType,
			});
		}
		setPdfUrl(null);
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setFormKey((prev) => prev + 1);
		setShowDrawer(false);

		setInvoiceFormData((prevFormData) => {
			if (prevFormData) {
				return { ...prevFormData, invoicePDFLink: undefined };
			}
			return prevFormData;
		});
	};

	const handleReset = useCallback(() => {
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setDisableCosts(false);

		// Reset form data to initial state with only workOrderId
		setInvoiceFormData({
			workOrderId: workOrderId,
			externalWorkOrderId: String(workOrderId),
			supplierFacilityId:
				workOrder && workOrder.supplierFacilityId ? workOrder.supplierFacilityId : undefined,
		});

		// Increment form key to force re-render
		setFormKey((prev) => prev + 1);

		// Track reset event
		posthog.capture('invoice_form_reset', {
			user_type: userType,
			had_parsed_data: !!parsedFormDataSnapshot,
		});
	}, [workOrderId, workOrder, userType, parsedFormDataSnapshot]);

	const handleFileSelected = useCallback((fileData) => {
		// Show PDF immediately when file is selected
		if (fileData.fileUrl) {
			setPdfUrl(fileData.fileUrl);
			setParsingLoading(true);
			setShowDrawer(true);
		} else {
			console.error('handleFileSelected: fileUrl is undefined');
		}
	}, []);

	const handleUploadError = useCallback((error = {}) => {
		message.error('Error parsing invoice. Please try again.');
		setParsingLoading(false);
		setShowDrawer(false);
		setFormKey((prev) => prev + 1);
		posthog.capture('invoice_pdf_parse_error', {
			user_type: userType,
			error: error.message,
		});
	}, []);

	const handleParsingStart = useCallback(() => {
		setParsingLoading(true);
	}, []);

	const handleParsingComplete = useCallback(() => {
		setParsingLoading(false);
	}, []);

	const handleUploadSuccess = useCallback(
		(data) => {
			if (data.parsedData && workOrder) {
				const adaptedFormData = adaptParsedInvoiceToFormData(data.parsedData, workOrder);
				if (adaptedFormData) {
					setInvoiceFormData((prevFormData) => {
						const mergedFormData = {
							...prevFormData,
							...adaptedFormData,
							id: prevFormData.id,
							status: prevFormData.status,
							supplierFacilityId: prevFormData.supplierFacilityId,
							buyerFacilityId: prevFormData.buyerFacilityId,
							invoicePDFLink: data.fileUrl,
						};

						setParsedFormDataSnapshot(adaptedFormData);
						setParsedDiscount(nullSafeGet('parsedData.discount', data));
						if (nullSafeGet('parsedData.discount', data) > 0) {
							setDisableCosts(true);
						} else {
							setDisableCosts(false);
						}

						const discount = nullSafeGet('parsedData.discount', data);
						const hasDiscount = (discount || 0) > 0;
						posthog.capture('invoice_pdf_parsed', {
							user_type: userType,
							has_parsed_data: true,
							has_discount: hasDiscount,
							...(hasDiscount && discount != null && { discount }),
						});

						return mergedFormData;
					});
				} else {
					posthog.capture('invoice_pdf_parsed', {
						user_type: userType,
						has_parsed_data: false,
						error: 'adaptation_failed',
					});
				}
			} else {
				posthog.capture('invoice_pdf_parsed', {
					user_type: userType,
					has_parsed_data: false,
					parsed_line_items_count: 0,
					parsed_fields: [],
					error: 'no_parsed_data',
				});
			}
			// Only update pdfUrl if a new one is provided (don't clear it)
			if (data.fileUrl) {
				setPdfUrl(data.fileUrl);
				setShowDrawer(true);
			}
			setFormKey((prev) => prev + 1);
		},
		[workOrder, userType]
	);

	const onSuccess = useCallback(
		(rec) => {
			history.replace(
				getLinkWIthBackLinkParams(
					{ ...location, pathname: `/${userType}/workOrders/detail/${workOrderId}/invoices` },
					'Back to Work Order',
					`/${userType}/invoices/detail/${rec.id}`
				)
			);
		},
		[history, userType, location, workOrderId]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			const isUpdate = values.id ? true : false;
			const submitAction = values.id ? updateInvoice : createInvoice;

			const finalWorkOrderId = workOrderId;

			const allFormValues = formInstance.getFieldsValue(true);

			const formValues = { ...allFormValues, ...values };

			console.log('Form values:', {
				formValues,
				values,
			});

			const hasLineItems = (items) => {
				return items && Array.isArray(items) && items.length > 0;
			};

			const laborLineItemsArray = formValues.laborLineItems || [];
			const laborLineItems = hasLineItems(laborLineItemsArray)
				? laborLineItemsArray.map((item) => ({
						roleType: item.roleType || '',
						numberOfTech: Number(item.numberOfTech || 0),
						seconds: Number(item.seconds || 0),
						hourlyRate: String(item.hourlyRate || 0),
						amount: String(item.amount || 0),
				  }))
				: [];

			const partsLineItemsArray = formValues.partsLineItems || [];
			const partsLineItems = hasLineItems(partsLineItemsArray)
				? partsLineItemsArray.map((item, index) => ({
						description: item.description || '',
						partId: item.partId || index + 1,
						partNumber: item.partNumber || '',
						unitType: item.unitType || '',
						quantity: item.quantity ? String(item.quantity) : '',
						unitPrice: item.unitPrice ? String(item.unitPrice) : '',
						amount: String(item.amount || 0),
				  }))
				: [];

			const equipmentLineItemsArray = formValues.equipmentLineItems || [];
			const equipmentLineItems = hasLineItems(equipmentLineItemsArray)
				? equipmentLineItemsArray.map((item, index) => ({
						description: item.description || '',
						equipmentId: item.equipmentId || index + 1,
						equipmentNumber: item.equipmentNumber || '',
						quantity: '1',
						unitType: 'each',
						unitPrice: item.unitPrice ? String(item.unitPrice) : undefined,
						amount: String(item.amount || 0),
				  }))
				: [];

			const materialLineItems = [...partsLineItems, ...equipmentLineItems];

			const travelLineItemsArray = formValues.travelLineItems || [];
			const travelLineItems = hasLineItems(travelLineItemsArray)
				? travelLineItemsArray.map((item) => ({
						description: item.description || '',
						amount: String(item.amount || 0),
						...(item.id && { id: item.id }),
						...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
				  }))
				: [];

			const freightLineItemsArray = formValues.freightLineItems || [];
			const freightLineItems = hasLineItems(freightLineItemsArray)
				? freightLineItemsArray.map((item) => ({
						description: item.description || '',
						amount: String(item.amount || 0),
						...(item.id && { id: item.id }),
						...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
				  }))
				: [];

			const miscLineItemsArray = formValues.miscLineItems || [];
			const miscLineItems = hasLineItems(miscLineItemsArray)
				? miscLineItemsArray.map((item) => ({
						description: item.description || '',
						amount: String(item.amount || 0),
						...(item.id && { id: item.id }),
						...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
				  }))
				: [];

			const taxKeys = formValues.taxKeys || [];
			let taxLineItems = [];

			if (taxKeys.length > 0) {
				taxLineItems = taxKeys.map((taxKey) => ({
					taxType: String(formValues[`tax-${taxKey}-taxType`] || ''),
					amount: getFixedFloatStringOrZero(Number(formValues[`tax-${taxKey}-amount`]) || 0),
				}));
			}

			const dateOfInvoice = formValues.dateOfInvoice
				? formValues.dateOfInvoice.format
					? formValues.dateOfInvoice.format('YYYY-MM-DD')
					: typeof formValues.dateOfInvoice === 'string'
					? formValues.dateOfInvoice
					: null
				: null;

			const dueDate = formValues.dueDate
				? formValues.dueDate.format
					? formValues.dueDate.format('YYYY-MM-DD')
					: typeof formValues.dueDate === 'string'
					? formValues.dueDate
					: null
				: null;

			const laborTotal = Number(formValues.laborTotal) || 0;
			const partsTotal = Number(formValues.partsTotal) || 0;
			const equipmentTotal = Number(formValues.equipmentTotal) || 0;
			const travelTotal = Number(formValues.travelTotal) || 0;
			const freightTotal = Number(formValues.freightTotal) || 0;
			const miscTotal = Number(formValues.miscTotal) || 0;

			const laborTotalBeforeTax = getFixedFloatStringOrZero(laborTotal);
			const materialTotalBeforeTax = getFixedFloatStringOrZero(partsTotal + equipmentTotal);
			const travelTotalBeforeTax = getFixedFloatStringOrZero(travelTotal);
			const freightTotalBeforeTax = getFixedFloatStringOrZero(freightTotal);
			const miscTotalBeforeTax = getFixedFloatStringOrZero(miscTotal);

			const invoiceTotalBeforeTax = getFixedFloatStringOrZero(
				Number(formValues.invoiceTotalBeforeTax) || 0
			);
			const invoiceTax = getFixedFloatStringOrZero(Number(formValues.invoiceTax) || 0);
			const invoiceTotalAfterTax = getFixedFloatStringOrZero(
				Number(formValues.invoiceTotalAfterTax) || 0
			);

			const invoice = {
				id: formValues.id,
				scope: scope !== null && scope !== undefined ? scope : '',
				externalWorkOrderId: formValues.externalWorkOrderId
					? `${formValues.externalWorkOrderId}`
					: `${workOrderId}`,
				invoiceNumber: formValues.invoiceNumber || '',
				paymentTerms: formValues.paymentTerms || '',
				dueDate: dueDate,
				dateOfInvoice: dateOfInvoice,
				invoiceTaxRate: formValues.invoiceTaxRate || null,
				invoiceTax: invoiceTax,
				invoiceTotalBeforeTax: invoiceTotalBeforeTax,
				invoiceTotalAfterTax: invoiceTotalAfterTax,
				laborTotalBeforeTax: laborTotalBeforeTax,
				laborLineItems: laborLineItems,
				...(taxKeys.length > 0 && { taxLineItems: taxLineItems }),
				materialTotalBeforeTax: materialTotalBeforeTax,
				materialLineItems: materialLineItems,
				freightTotalBeforeTax: freightTotalBeforeTax,
				freightLineItems: freightLineItems,
				travelTotalBeforeTax: travelTotalBeforeTax,
				travelLineItems: travelLineItems,
				miscTotalBeforeTax: miscTotalBeforeTax,
				miscLineItems: miscLineItems,
				workOrderId: parseInt(finalWorkOrderId, 10),
				createdBy: currentUser.email,
				attachments: formValues.attachments || [],
				status: formValues.status || 'pending',
				invoicePDFLink: invoiceFormData.invoicePDFLink || undefined,
				isDeleted: invoiceFormData && invoiceFormData.isDeleted ? true : false,
				buyerFacilityId: formValues.buyerFacilityId || nullSafeGet('facility.id', currentUser),
				supplierFacilityId: invoiceFormData && invoiceFormData.supplierFacilityId,
				...(formValues.signatureName && { aiParseMismatchSignedByEmail: currentUser.email }),
			};

			try {
				if (parsedFormDataSnapshot) {
					const changeMetrics = compareParsedWithFinal(parsedFormDataSnapshot, invoice);
					const hadEdits =
						nullSafeGet('total_fields_changed', changeMetrics) > 0 ||
						nullSafeGet('total_line_items_changed', changeMetrics) > 0;

					if (isUpdate) {
						posthog.capture('invoice_updated_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					} else {
						posthog.capture('invoice_created_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					}
					if (hadEdits) {
						invoice.isAiParsedInvoiceEdited = true;
						invoice.isAiParsed = false;
					} else {
						invoice.isAiParsed = true;
						invoice.isAiParsedInvoiceEdited = false;
					}
				}
				const record = await submitAction(invoice);

				if (onSuccess) {
					onSuccess(record);
				}
			} catch (err) {
				if (
					typeof err === 'string' &&
					err.indexOf('Invoice(s) are already associated with this work order') === 0
				) {
					message.error(err);
					history.goBack();
				}
				throw err;
			}
		},
		[
			updateInvoice,
			createInvoice,
			invoiceFormData,
			currentUser,
			onSuccess,
			history,
			parsedFormDataSnapshot,
			userType,
			workOrderId,
		]
	);

	const formConfig = useMemo(() => {
		if (!invoiceFormData || !workOrder || !currentUser) {
			return null;
		}

		return createBuyerInvoiceConfig(
			invoiceFormData,
			workOrder,
			currentUser,
			history,
			handleSubmit,
			{
				creating,
				updating,
				createErrors,
				updateErrors,
				parsedDataSnapshot: parsedFormDataSnapshot,
				disableCosts: disableCosts,
				onReset: handleReset,
				fetchTaxLineItems,
			}
		);
	}, [
		invoiceFormData,
		workOrder,
		currentUser,
		history,
		handleSubmit,
		creating,
		updating,
		createErrors,
		updateErrors,
		parsedFormDataSnapshot,
		disableCosts,
		handleReset,
		fetchTaxLineItems,
	]);

	if (invoiceFormDataLoading || !formConfig) {
		return <PageLoadingPlaceholder />;
	}

	return (
		<Content>
			<InvoiceUploadandParse
				handleUploadSuccess={handleUploadSuccess}
				onFileSelected={handleFileSelected}
				onParsingStart={handleParsingStart}
				onParsingComplete={handleParsingComplete}
				uploaderType="dragger"
				onError={handleUploadError}
			/>
			<Drawer
				width="100vw"
				placement="right"
				visible={showDrawer}
				bodyStyle={{ height: '100vh', padding: 0, overflow: 'hidden' }}
				style={{ top: 0, height: '100vh' }}
				className="fullscreen-drawer"
				headerStyle={{ display: 'none' }}
			>
				<Row>
					<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
					<Col span={24}>
						<Row style={{ minHeight: '100vh' }} align="stretch" gutter={8}>
							<Col span={12} style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={pdfUrl}
										fileName="Invoice.pdf"
										showDelete={true}
										onDelete={handleDeletePdf}
									/>
								</div>
							</Col>
							<Col
								span={12}
								style={{
									height: '100%',
									padding: 0,
									overflowY: 'auto',
									maxHeight: '100vh',
								}}
							>
								<div className="h-full p-6">
									<Popconfirm
										title="Are you sure you want to go back? Any unsaved changes will be lost."
										onConfirm={() => {
											handleDeletePdf();
											setShowDrawer(false);
										}}
										okText="Yes"
										cancelText="No"
									>
										<Button type="ghost" icon={<ArrowLeftOutlined />} className="mb-2">
											Back
										</Button>
									</Popconfirm>
									{parsingLoading ? (
										<InvoiceLoadingSkeletonAnimated />
									) : (
										<div>
											<div className="h3medium mb-4">Create Invoice</div>

											<InvoiceFormBase
												key={formKey}
												config={formConfig}
												handleCancel={handleDeletePdf}
											/>
										</div>
									)}
								</div>
							</Col>
						</Row>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	creating: state.invoices.creating,
	updating: state.invoices.updating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
	createInvoiceWithWorkOrderId: (workOrderId, entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? createInvoiceForSupplierBasedOnWorkOrderForSupplier(entity, workOrderId)
				: createInvoiceForSupplierBasedOnWorkOrderForBuyer(entity, workOrderId)
		),
	createInvoice: (invoice) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? createInvoiceByInternalTech(invoice)
				: invoicesRestCrudThunksForBuyer.create(invoice)
		),
	updateInvoice: (invoice) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? invoicesRestCrudThunksForSupplier.update(invoice)
				: invoicesRestCrudThunksForBuyer.update(invoice)
		),
	fetchTaxLineItems: (currencyId: string) => dispatch(fetchTaxLineItemsForBuyer(currencyId)),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(NewBuyerInvoicePageV2))
);
